import { API, graphqlOperation } from 'aws-amplify';
import { listMetricData, metricDataByClientDate } from '../graphql/queries';

export async function fetchMetricData(filter, limit = 1000) {
  let nextToken = null;
  let allItems = [];

  do {
    try {
      const response = await API.graphql(graphqlOperation(listMetricData, {
        filter,
        limit,
        nextToken
      }));

      const { items, nextToken: newNextToken } = response.data.listMetricData;
      allItems = allItems.concat(items);
      nextToken = newNextToken;
    } catch (error) {
      console.error('Error fetching metric data:', error);
      throw error;
    }
  } while (nextToken);

  return allItems;
}

/**
 * @function fetchMetricDataByClientDate
 * @description
 * Fetches all MetricData records using the `metricDataByClientDate` index query,
 * handling pagination via `nextToken`. This ensures all matching records across
 * multiple pages are retrieved, not just the first batch.
 *
 * The query requires `clientId` as the partition key, and `date` as a string key
 * condition input (e.g., `{ eq: '2025-06-03' }` or `{ between: ['2025-06-03', '2025-06-09'] }`).
 * Optional `filter` input allows further narrowing by `storeId`, `metricName`, etc.
 *
 * @param {Object} params
 * @param {string} params.clientId - The clientId used as the partition key for the index.
 * @param {Object} params.date - The date condition in `ModelStringKeyConditionInput` format.
 *        E.g., `{ eq: '2025-06-03' }` or `{ between: ['2025-06-03', '2025-06-09'] }`
 * @param {Object} [params.filter] - Optional filter to refine results (e.g., by storeId, metricName).
 * @param {number} [params.limit=1000] - Max items per page (default: 1000).
 * @returns {Promise<Array>} Resolves to an array of all matching MetricData records.
 *
 * @example
 * const items = await fetchMetricDataByClientDate({
 *   clientId: 'grm',
 *   date: { eq: '2025-06-03' },
 *   filter: {
 *     storeId: { eq: 'abc-123' },
 *     or: [
 *       { metricName: { eq: 'checkCount' } },
 *       { metricName: { eq: 'netSales' } }
 *     ]
 *   }
 * })
 */

export async function fetchMetricDataByClientDate({ clientId, date, filter, limit = 1000 }) {

  let nextToken = null;
  let allItems = [];

  do {
    try {
      const response = await API.graphql(graphqlOperation(metricDataByClientDate, {
        clientId,
        date,
        filter,
        limit,
        nextToken
      }));

      const { items, nextToken: newNextToken } = response.data.metricDataByClientDate;
      allItems = allItems.concat(items);
      nextToken = newNextToken;
    } catch (error) {
      console.error('Error fetching paginated metric data:', error);
      throw error;
    }
  } while (nextToken);

  return allItems;
}